import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '../../components/fluxograma/fluxograma.css'
import {
  FlowAiChat,
  FlowCanvas,
  FlowPalette,
  FlowToolbar,
  PropertiesPanel,
  ConnectionSettings,
  useFlowStore,
  type SaveStatus,
} from '../../components/fluxograma'
import { Button, Heading, LoadingSpinner, Text } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useTheme } from '../../hooks/useTheme'
import { useUserLogs } from '../../hooks/useUserLogs'
import { sendFlowchartChatMessage } from '../../services/mcpFlowchartService'
import {
  createFlowchart,
  getFlowchartById,
  getFlowchartVersion,
  publishFlowchart,
  updateFlowchart,
} from '../../services/qualityFlowcharts'
import type { FlowchartAiMessage } from '../../types/flowchartAi'
import { validateFlowGraphFromAi } from '../../types/flowchartAi'
import type {
  QualityFlowchartGraph,
  QualityFlowchartStatus,
} from '../../types/qualityFlowchart'
import { MODULE_NAME } from './Fluxograma'

const LOG_ORIGEM = 'qualidade/fluxograma'
const ENTITY_TYPE = 'process_flowchart' as const
const LOCAL_AI_CHAT_ID = 'local-flowchart-assistant'
const WELCOME_AI_MESSAGE =
  'Olá! Descreva o processo que você quer mapear. Posso fazer perguntas e desenhar o fluxograma na tela conforme a gente conversa. Se já houver fluxo no canvas, peça alterações e eu atualizo na hora.'

function makeLocalAiMessage(
  role: FlowchartAiMessage['role'],
  content: string,
  idPrefix = 'msg'
): FlowchartAiMessage {
  return {
    id: `${idPrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    chat_id: LOCAL_AI_CHAT_ID,
    role,
    content,
    created_at: new Date().toISOString(),
  }
}

const FluxogramaEditorInner: React.FC<{
  flowchartId: string | null
  isNew: boolean
  startInPresentation: boolean
  versionNumber: number | null
}> = ({
  flowchartId: initialId,
  isNew,
  startInPresentation,
  versionNumber,
}) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { darkMode } = useTheme()
  const { hasPermission: canInsert, loading: loadingInsert } = usePermission(
    MODULE_NAME,
    'insert'
  )
  const { hasPermission: canUpdate, loading: loadingUpdate } = usePermission(
    MODULE_NAME,
    'update'
  )
  const permissionsReady = !loadingInsert && !loadingUpdate
  const { logCreate, logUpdate } = useUserLogs()

  const loadGraph = useFlowStore((s) => s.loadGraph)
  const resetStore = useFlowStore((s) => s.resetStore)
  const setColorMode = useFlowStore((s) => s.setColorMode)
  const setReadOnly = useFlowStore((s) => s.setReadOnly)
  const setPresentationMode = useFlowStore((s) => s.setPresentationMode)
  const presentationMode = useFlowStore((s) => s.presentationMode)
  const dirty = useFlowStore((s) => s.dirty)
  const markClean = useFlowStore((s) => s.markClean)
  const getGraphForSave = useFlowStore((s) => s.getGraphForSave)
  const setGraph = useFlowStore((s) => s.setGraph)
  const setEdgeType = useFlowStore((s) => s.setEdgeType)
  const setViewportState = useFlowStore((s) => s.setViewportState)
  const flowName = useFlowStore((s) => s.flowName)

  const [flowchartId, setFlowchartId] = useState<string | null>(initialId)
  const [status, setStatus] = useState<QualityFlowchartStatus>('draft')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [ready, setReady] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [viewingHistoricalVersion, setViewingHistoricalVersion] = useState(
    versionNumber != null
  )
  const [leaveModalOpen, setLeaveModalOpen] = useState(false)
  const [pendingLeavePath, setPendingLeavePath] = useState<string | null>(null)
  const [leaveAction, setLeaveAction] = useState<'idle' | 'saving-draft'>(
    'idle'
  )

  const [aiAssistantActive, setAiAssistantActive] = useState(false)
  const [aiMessages, setAiMessages] = useState<FlowchartAiMessage[]>([])
  const [aiChatOpen, setAiChatOpen] = useState(false)
  const [creatingAiChat, setCreatingAiChat] = useState(false)
  const [sendingAiMessage, setSendingAiMessage] = useState(false)
  const [aiChatError, setAiChatError] = useState<string | null>(null)
  const aiAbortRef = useRef<AbortController | null>(null)
  const aiOpenedFromQueryRef = useRef(false)

  const lastSavedTitleRef = useRef('')
  const hydrateDoneRef = useRef(false)
  const statusRef = useRef<QualityFlowchartStatus>('draft')
  const allowLeaveRef = useRef(false)
  const flowchartIdRef = useRef<string | null>(initialId)
  const persistChainRef = useRef<Promise<unknown>>(Promise.resolve())
  /** Evita resetStore/reload ao trocar /novo → /:id após o primeiro save */
  const skipHydrateReloadRef = useRef(false)
  /** Bloqueia persists extras durante saída */
  const suspendPersistRef = useRef(false)
  const draftSaveInFlightRef = useRef<Promise<boolean> | null>(null)
  /** Detecta saída da apresentação (true→false) para limpar a URL sem corrida no mount */
  const wasPresentationRef = useRef(false)

  const canEdit =
    !viewingHistoricalVersion &&
    (isNew ? canInsert || canUpdate : canUpdate || canInsert)

  // useBlocker exige createBrowserRouter; o app usa BrowserRouter.
  // Em apresentação/somente leitura não há edição — não bloquear saída.
  // Bloqueia apenas com alterações não salvas (rascunho e publicado usam save manual).
  const shouldBlockLeave =
    canEdit &&
    !presentationMode &&
    !viewingHistoricalVersion &&
    !allowLeaveRef.current &&
    dirty

  useEffect(() => {
    statusRef.current = status
  }, [status])

  useEffect(() => {
    flowchartIdRef.current = flowchartId
  }, [flowchartId])

  useEffect(() => {
    setColorMode(darkMode ? 'dark' : 'light')
  }, [darkMode, setColorMode])

  useEffect(() => {
    if (!permissionsReady) return
    setReadOnly(!canEdit)
    if (!canEdit || startInPresentation || viewingHistoricalVersion) {
      setPresentationMode(true)
    } else {
      setPresentationMode(false)
    }
  }, [
    permissionsReady,
    canEdit,
    startInPresentation,
    viewingHistoricalVersion,
    setReadOnly,
    setPresentationMode,
  ])

  // Limpa ?modo=apresentacao só quando o usuário SAI da apresentação (toolbar Editar),
  // nunca no mount — senão a URL some antes do modo ser aplicado e abre em edição.
  useEffect(() => {
    const wasPresenting = wasPresentationRef.current
    wasPresentationRef.current = presentationMode

    if (!canUpdate || !wasPresenting || presentationMode) return

    const hadApresentacao = searchParams.get('modo') === 'apresentacao'
    const hadVersao = Boolean(searchParams.get('versao'))
    if (!hadApresentacao && !hadVersao) return

    const next = new URLSearchParams(searchParams)
    next.delete('modo')
    next.delete('versao')
    setSearchParams(next, { replace: true })

    if (hadVersao && initialId) {
      setViewingHistoricalVersion(false)
      void (async () => {
        try {
          const row = await getFlowchartById(initialId)
          if (!row) return
          const graph = row.flow_json as QualityFlowchartGraph
          loadGraph({
            nodes: (graph.nodes || []) as never[],
            edges: (graph.edges || []) as never[],
            viewport: graph.viewport,
            edgeType: graph.edgeType ?? 'smoothstep',
            name: row.title,
          })
          lastSavedTitleRef.current = row.title
          setStatus(row.status)
          markClean()
        } catch (err) {
          console.error(err)
        }
      })()
    }
  }, [
    canUpdate,
    presentationMode,
    searchParams,
    setSearchParams,
    initialId,
    loadGraph,
    markClean,
  ])

  // Ao abrir com ?modo=edicao, remove o query após aplicar o modo
  useEffect(() => {
    if (
      permissionsReady &&
      canEdit &&
      !presentationMode &&
      !startInPresentation &&
      searchParams.get('modo') === 'edicao'
    ) {
      const next = new URLSearchParams(searchParams)
      next.delete('modo')
      setSearchParams(next, { replace: true })
    }
  }, [
    permissionsReady,
    canEdit,
    presentationMode,
    startInPresentation,
    searchParams,
    setSearchParams,
  ])

  // Assistente local (MCP) — não carrega chat Tess/n8n
  useEffect(() => {
    if (viewingHistoricalVersion) {
      setAiAssistantActive(false)
      setAiMessages([])
      setAiChatOpen(false)
    }
    return () => {
      aiAbortRef.current?.abort()
    }
  }, [viewingHistoricalVersion])

  const openAiAssistant = useCallback(
    (opts?: { forceWelcome?: boolean }) => {
      if (!canEdit || viewingHistoricalVersion) return
      setAiChatError(null)
      setAiAssistantActive(true)
      setAiMessages((prev) => {
        if (prev.length > 0 && !opts?.forceWelcome) return prev
        return [makeLocalAiMessage('assistant', WELCOME_AI_MESSAGE, 'welcome')]
      })
      setAiChatOpen(true)
    },
    [canEdit, viewingHistoricalVersion]
  )

  // ?ia=1 → abre o assistente ao montar o editor (ex.: botão "Criar com IA")
  useEffect(() => {
    if (!permissionsReady || !ready || !canEdit || viewingHistoricalVersion) return
    if (searchParams.get('ia') !== '1') return
    if (aiOpenedFromQueryRef.current) return
    aiOpenedFromQueryRef.current = true
    openAiAssistant({ forceWelcome: true })
    const next = new URLSearchParams(searchParams)
    next.delete('ia')
    setSearchParams(next, { replace: true })
  }, [
    permissionsReady,
    ready,
    canEdit,
    viewingHistoricalVersion,
    searchParams,
    setSearchParams,
    openAiAssistant,
  ])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      // Create acabou de navegar /novo → /id: mantém o canvas, não recarrega
      if (
        skipHydrateReloadRef.current &&
        initialId &&
        flowchartIdRef.current === initialId
      ) {
        skipHydrateReloadRef.current = false
        hydrateDoneRef.current = true
        setFlowchartId(initialId)
        setReady(true)
        setLoading(false)
        setError(null)
        return
      }

      // Evita falso "rascunho indisponível" enquanto permissões ainda carregam
      if (!permissionsReady) {
        setLoading(true)
        return
      }

      setLoading(true)
      setError(null)
      hydrateDoneRef.current = false
      resetStore()
      setColorMode(darkMode ? 'dark' : 'light')

      const historical = versionNumber != null && !isNew && Boolean(initialId)
      setViewingHistoricalVersion(historical)
      const editAllowed =
        !historical && (isNew ? canInsert || canUpdate : canUpdate || canInsert)
      setReadOnly(!editAllowed)
      if (!editAllowed || historical) {
        setPresentationMode(true)
      } else {
        setPresentationMode(false)
      }

      try {
        if (isNew || !initialId) {
          loadGraph({
            nodes: [],
            edges: [],
            viewport: null,
            edgeType: 'smoothstep',
            name: 'Novo fluxograma',
          })
          lastSavedTitleRef.current = 'Novo fluxograma'
          setFlowchartId(null)
          flowchartIdRef.current = null
          setStatus('draft')
          setPresentationMode(false)
        } else if (historical && versionNumber != null) {
          const version = await getFlowchartVersion(initialId, versionNumber)
          if (!version) {
            throw new Error(`Versão ${versionNumber} não encontrada`)
          }
          const row = await getFlowchartById(initialId)
          if (row && row.status !== 'published') {
            throw new Error(
              'Somente fluxogramas publicados podem ser apresentados'
            )
          }
          const graph = version.flow_json as QualityFlowchartGraph
          loadGraph({
            nodes: (graph.nodes || []) as never[],
            edges: (graph.edges || []) as never[],
            viewport: graph.viewport,
            edgeType: graph.edgeType ?? 'smoothstep',
            name: `${version.title} (v${version.version_number})`,
          })
          lastSavedTitleRef.current = version.title
          setFlowchartId(initialId)
          flowchartIdRef.current = initialId
          setStatus(row?.status ?? 'published')
          setPresentationMode(true)
        } else {
          const row = await getFlowchartById(initialId)
          if (!row) {
            throw new Error('Fluxograma não encontrado')
          }
          if (row.status === 'draft' && !canUpdate && !canInsert) {
            throw new Error(
              'Este fluxograma está em rascunho e não está disponível para visualização'
            )
          }
          const graph = row.flow_json as QualityFlowchartGraph
          loadGraph({
            nodes: (graph.nodes || []) as never[],
            edges: (graph.edges || []) as never[],
            viewport: graph.viewport,
            edgeType: graph.edgeType ?? 'smoothstep',
            name: row.title,
          })
          lastSavedTitleRef.current = row.title
          setFlowchartId(row.id)
          flowchartIdRef.current = row.id
          setStatus(row.status)
          const allowPresentation =
            row.status === 'published' &&
            (startInPresentation || !editAllowed)
          setPresentationMode(allowPresentation)
          if (startInPresentation && row.status !== 'published') {
            const next = new URLSearchParams(searchParams)
            next.delete('modo')
            setSearchParams(next, { replace: true })
          }
        }
        if (!cancelled) {
          markClean()
          setReady(true)
          window.setTimeout(() => {
            if (!cancelled) {
              hydrateDoneRef.current = true
            }
          }, 100)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
      if (!skipHydrateReloadRef.current) {
        resetStore()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when route or permissions settle
  }, [
    initialId,
    isNew,
    versionNumber,
    startInPresentation,
    permissionsReady,
    canInsert,
    canUpdate,
  ])

  const persist = useCallback(
    async (opts?: {
      createVersion?: boolean
      skipNavigate?: boolean
      /** Permite salvar mesmo durante breve rehydrate (ex.: sair do editor) */
      allowDuringHydrate?: boolean
      /** Sempre grava o grafo atual (não confia só no flag dirty) */
      force?: boolean
      /** Snapshot capturado no clique (antes de awaits da fila) */
      graphOverride?: ReturnType<typeof getGraphForSave>
    }): Promise<boolean> => {
      const run = async (): Promise<boolean> => {
        // Durante saída, só o flush forçado pode gravar
        if (suspendPersistRef.current && !opts?.force) {
          return true
        }
        if (!canEdit) return false
        if (!hydrateDoneRef.current && !opts?.allowDuringHydrate) return false

        // Já salvo e com id: nada a fazer — exceto flush forçado ao sair
        if (
          !opts?.force &&
          !opts?.graphOverride &&
          flowchartIdRef.current &&
          !useFlowStore.getState().dirty
        ) {
          return true
        }

        if (!flowchartIdRef.current && !canInsert) {
          setSaveStatus('error')
          setError('Sem permissão para criar fluxograma')
          return false
        }

        const graph = opts?.graphOverride ?? getGraphForSave()
        const title = graph.name?.trim() || 'Novo fluxograma'
        const flow_json: QualityFlowchartGraph = {
          nodes: graph.nodes,
          edges: graph.edges,
          viewport: graph.viewport,
          edgeType: graph.edgeType ?? 'smoothstep',
        }
        const isPublished = statusRef.current === 'published'
        const createVersion =
          opts?.createVersion ?? (isPublished ? true : false)

        setSaveStatus('saving')
        try {
          if (!flowchartIdRef.current) {
            const created = await createFlowchart({ title, flow_json })
            await logCreate(
              ENTITY_TYPE,
              created.id,
              `Criou fluxograma "${created.title}"`,
              {
                origem: LOG_ORIGEM,
                id: created.id,
                title: created.title,
                description: created.description,
                status: created.status,
              }
            )
            flowchartIdRef.current = created.id
            setFlowchartId(created.id)
            setStatus(created.status)
            statusRef.current = created.status
            lastSavedTitleRef.current = created.title
            // Só limpa dirty se o canvas ainda é o mesmo snapshot salvo
            const latest = getGraphForSave()
            if (
              latest.nodes === graph.nodes &&
              latest.edges === graph.edges &&
              latest.viewport === graph.viewport &&
              latest.edgeType === graph.edgeType &&
              latest.name === graph.name
            ) {
              markClean()
            }
            setSaveStatus('saved')
            if (!opts?.skipNavigate) {
              skipHydrateReloadRef.current = true
              navigate(`/qualidade/fluxograma/${created.id}`, { replace: true })
            }
            return true
          }

          const id = flowchartIdRef.current
          const updated = await updateFlowchart(id, {
            title,
            flow_json,
            createVersion,
          })
          if (title !== lastSavedTitleRef.current) {
            await logUpdate(
              ENTITY_TYPE,
              id,
              `Atualizou fluxograma "${title}"`,
              { title: lastSavedTitleRef.current },
              { title },
              { origem: LOG_ORIGEM, id }
            )
            lastSavedTitleRef.current = title
          } else if (createVersion) {
            await logUpdate(
              ENTITY_TYPE,
              id,
              `Salvou versão do fluxograma "${title}"`,
              undefined,
              { current_version: updated.current_version },
              { origem: LOG_ORIGEM, id }
            )
          }
          setStatus(updated.status)
          statusRef.current = updated.status
          const latest = getGraphForSave()
          if (
            latest.nodes === graph.nodes &&
            latest.edges === graph.edges &&
            latest.viewport === graph.viewport &&
            latest.edgeType === graph.edgeType &&
            latest.name === graph.name
          ) {
            markClean()
          }
          setSaveStatus('saved')
          return true
        } catch (err) {
          console.error(err)
          setSaveStatus('error')
          setError(
            err instanceof Error ? err.message : 'Erro ao salvar fluxograma'
          )
          return false
        }
      }

      // Serializa saves manuais + sair/publicar para não abortar uns aos outros
      const next = persistChainRef.current.then(run, run)
      persistChainRef.current = next.then(
        () => undefined,
        () => undefined
      )
      return next
    },
    [
      canEdit,
      canInsert,
      getGraphForSave,
      logCreate,
      logUpdate,
      markClean,
      navigate,
    ]
  )

  const publishCurrent = useCallback(async (): Promise<boolean> => {
    if (!canEdit || publishing) return false

    if (!flowchartIdRef.current) {
      const created = await persist({ createVersion: false, skipNavigate: true })
      if (!created || !flowchartIdRef.current) return false
    }

    const id = flowchartIdRef.current
    if (!id) return false

    setPublishing(true)
    setSaveStatus('saving')
    try {
      const graph = getGraphForSave()
      const title = graph.name?.trim() || 'Novo fluxograma'
      const flow_json: QualityFlowchartGraph = {
        nodes: graph.nodes,
        edges: graph.edges,
        viewport: graph.viewport,
        edgeType: graph.edgeType ?? 'smoothstep',
      }
      const published = await publishFlowchart(id, {
        title,
        flow_json,
      })
      await logUpdate(
        ENTITY_TYPE,
        id,
        `Publicou fluxograma "${published.title}"`,
        { status: 'draft' },
        { status: 'published', current_version: published.current_version },
        { origem: LOG_ORIGEM }
      )
      setStatus('published')
      statusRef.current = 'published'
      lastSavedTitleRef.current = published.title
      markClean()
      setSaveStatus('saved')
      return true
    } catch (err) {
      console.error(err)
      setSaveStatus('error')
      setError(err instanceof Error ? err.message : 'Erro ao publicar')
      return false
    } finally {
      setPublishing(false)
    }
  }, [
    canEdit,
    publishing,
    persist,
    getGraphForSave,
    logUpdate,
    markClean,
  ])

  const handlePublish = useCallback(async () => {
    await publishCurrent()
  }, [publishCurrent])

  const captureGraphSnapshot = useCallback(() => {
    const raw = getGraphForSave()
    return {
      name: raw.name,
      nodes: JSON.parse(JSON.stringify(raw.nodes ?? [])) as unknown[],
      edges: JSON.parse(JSON.stringify(raw.edges ?? [])) as unknown[],
      viewport: raw.viewport ? { ...raw.viewport } : null,
      edgeType: raw.edgeType,
    }
  }, [getGraphForSave])

  const saveDraftSnapshot = useCallback(
    async (
      snapshot: {
        name?: string
        nodes: unknown[]
        edges: unknown[]
        viewport: QualityFlowchartGraph['viewport']
        edgeType?: QualityFlowchartGraph['edgeType']
      },
      options?: { suspendOtherPersists?: boolean }
    ): Promise<boolean> => {
      if (draftSaveInFlightRef.current) {
        try {
          await draftSaveInFlightRef.current
        } catch {
          /* ignore */
        }
      }

      const run = async (): Promise<boolean> => {
        if (options?.suspendOtherPersists) {
          suspendPersistRef.current = true
        }

        const title = snapshot.name?.trim() || 'Novo fluxograma'
        const flow_json: QualityFlowchartGraph = {
          nodes: snapshot.nodes,
          edges: snapshot.edges,
          viewport: snapshot.viewport,
          edgeType: snapshot.edgeType ?? 'smoothstep',
        }

        setSaveStatus('saving')
        try {
          let id = flowchartIdRef.current
          if (!id) {
            if (!canInsert) {
              setError('Sem permissão para criar fluxograma')
              setSaveStatus('error')
              if (options?.suspendOtherPersists) {
                suspendPersistRef.current = false
              }
              return false
            }
            const created = await createFlowchart({ title, flow_json })
            id = created.id
            flowchartIdRef.current = id
            setFlowchartId(id)
            setStatus(created.status)
            statusRef.current = created.status
            lastSavedTitleRef.current = created.title
            void logCreate(
              ENTITY_TYPE,
              created.id,
              `Criou fluxograma "${created.title}"`,
              {
                origem: LOG_ORIGEM,
                id: created.id,
                title: created.title,
                description: created.description,
                status: created.status,
              }
            ).catch((err) => console.error(err))
          } else {
            await updateFlowchart(id, {
              title,
              flow_json,
              createVersion: false,
            })
            lastSavedTitleRef.current = title
          }

          const verify = await getFlowchartById(id)
          const savedCount = Array.isArray(verify?.flow_json?.nodes)
            ? verify!.flow_json.nodes.length
            : 0
          if (savedCount !== flow_json.nodes.length) {
            throw new Error(
              `Falha ao confirmar save (enviados ${flow_json.nodes.length} nós, gravados ${savedCount})`
            )
          }

          markClean()
          setSaveStatus('saved')
          setError(null)

          if (
            typeof window !== 'undefined' &&
            window.location.pathname.endsWith('/novo')
          ) {
            skipHydrateReloadRef.current = true
            navigate(`/qualidade/fluxograma/${id}`, { replace: true })
          }
          return true
        } catch (err) {
          console.error(err)
          setSaveStatus('error')
          setError(
            err instanceof Error ? err.message : 'Erro ao salvar rascunho'
          )
          if (options?.suspendOtherPersists) {
            suspendPersistRef.current = false
          }
          return false
        }
      }

      const pending = run().finally(() => {
        if (draftSaveInFlightRef.current === pending) {
          draftSaveInFlightRef.current = null
        }
      })
      draftSaveInFlightRef.current = pending
      return pending
    },
    [canInsert, logCreate, markClean, navigate]
  )

  const handleManualSave = useCallback(async () => {
    if (statusRef.current === 'draft') {
      const snap = captureGraphSnapshot()
      await saveDraftSnapshot(snap)
      return
    }
    await persist({ createVersion: true })
  }, [persist, saveDraftSnapshot, captureGraphSnapshot])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldBlockLeave) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [shouldBlockLeave])

  useEffect(() => {
    if (!shouldBlockLeave) return

    const onDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = (event.target as HTMLElement | null)?.closest?.(
        'a[href]'
      ) as HTMLAnchorElement | null
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:')) return
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return

      let url: URL
      try {
        url = new URL(href, window.location.origin)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return

      const nextPath = `${url.pathname}${url.search}${url.hash}`
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
      if (nextPath === currentPath) return

      event.preventDefault()
      event.stopPropagation()
      setPendingLeavePath(`${url.pathname}${url.search}`)
      setLeaveModalOpen(true)
    }

    document.addEventListener('click', onDocumentClick, true)
    return () => document.removeEventListener('click', onDocumentClick, true)
  }, [shouldBlockLeave])

  const requestLeave = useCallback(
    async (path = '/qualidade/fluxograma') => {
      // Apresentação / somente leitura: sair direto, sem modal
      if (presentationMode || !canEdit || viewingHistoricalVersion) {
        allowLeaveRef.current = true
        navigate(path)
        return
      }
      if (!shouldBlockLeave) {
        allowLeaveRef.current = true
        navigate(path)
        return
      }
      setPendingLeavePath(path)
      setLeaveModalOpen(true)
    },
    [
      presentationMode,
      viewingHistoricalVersion,
      canEdit,
      shouldBlockLeave,
      navigate,
    ]
  )

  const confirmLeaveWithoutSave = useCallback(async () => {
    markClean()
    setLeaveModalOpen(false)
    allowLeaveRef.current = true
    if (pendingLeavePath) {
      navigate(pendingLeavePath)
    }
    setPendingLeavePath(null)
  }, [markClean, navigate, pendingLeavePath])

  const confirmSaveAndLeave = useCallback(async () => {
    const isDraft = statusRef.current === 'draft'
    setLeaveAction('saving-draft')
    setError(null)
    try {
      const ok = isDraft
        ? await saveDraftSnapshot(captureGraphSnapshot(), {
            suspendOtherPersists: true,
          })
        : await persist({ createVersion: true, skipNavigate: true })
      if (!ok) return
      setLeaveModalOpen(false)
      allowLeaveRef.current = true
      if (pendingLeavePath) {
        navigate(pendingLeavePath)
      }
      setPendingLeavePath(null)
    } finally {
      setLeaveAction('idle')
    }
  }, [
    saveDraftSnapshot,
    captureGraphSnapshot,
    persist,
    navigate,
    pendingLeavePath,
  ])

  const cancelLeave = useCallback(() => {
    if (leaveAction !== 'idle') return
    suspendPersistRef.current = false
    setLeaveModalOpen(false)
    setPendingLeavePath(null)
  }, [leaveAction])

  const handleCreateAiChat = useCallback(() => {
    if (!canEdit || creatingAiChat || viewingHistoricalVersion) return
    setCreatingAiChat(true)
    try {
      openAiAssistant({ forceWelcome: aiMessages.length === 0 })
    } finally {
      setCreatingAiChat(false)
    }
  }, [
    canEdit,
    creatingAiChat,
    viewingHistoricalVersion,
    openAiAssistant,
    aiMessages.length,
  ])

  const handleSendAiMessage = useCallback(
    async (message: string) => {
      if (!canEdit || sendingAiMessage || viewingHistoricalVersion) return
      setSendingAiMessage(true)
      setAiChatError(null)

      const optimisticUser = makeLocalAiMessage('user', message, 'user')
      const historyForApi = [
        ...aiMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
        { role: 'user' as const, content: message },
      ]

      setAiMessages((prev) => [...prev, optimisticUser])

      aiAbortRef.current?.abort()
      const controller = new AbortController()
      aiAbortRef.current = controller

      try {
        const graph = getGraphForSave()
        const result = await sendFlowchartChatMessage({
          messages: historyForApi,
          graph: {
            nodes: graph.nodes,
            edges: graph.edges,
            viewport: graph.viewport,
            edgeType: graph.edgeType,
          },
          signal: controller.signal,
        })

        let assistantContent = result.mensagem
        const validated = result.graph
          ? validateFlowGraphFromAi(result.graph)
          : null

        if (validated) {
          setGraph(validated.nodes, validated.edges, true)
          if (validated.edgeType) setEdgeType(validated.edgeType)
          if (validated.viewport) setViewportState(validated.viewport)
        } else if (result.graph) {
          assistantContent = `${result.mensagem}\n\n(Observação: o grafo retornado era inválido e não foi aplicado ao canvas.)`
          setAiChatError(
            'A IA respondeu, mas o grafo retornado é inválido e não foi aplicado.'
          )
        }

        setAiMessages((prev) => [
          ...prev,
          makeLocalAiMessage('assistant', assistantContent, 'assistant'),
        ])
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return
        setAiMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id))
        setAiChatError(
          err instanceof Error
            ? err.message
            : 'Erro ao enviar mensagem ao assistente.'
        )
      } finally {
        setSendingAiMessage(false)
      }
    },
    [
      canEdit,
      sendingAiMessage,
      viewingHistoricalVersion,
      aiMessages,
      getGraphForSave,
      setGraph,
      setEdgeType,
      setViewportState,
    ]
  )

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error && !flowchartId) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
        <Text size="lg" className="text-red-600 dark:text-red-400">
          {error}
        </Text>
        <button
          type="button"
          className="text-sm text-[#008B95] underline"
          onClick={() => navigate('/qualidade/fluxograma')}
        >
          Voltar à lista
        </button>
      </div>
    )
  }

  const leaveBusy = leaveAction !== 'idle'

  return (
    <div
      className="fluxograma-editor flex-1 min-h-0"
      data-theme={darkMode ? 'dark' : 'light'}
    >
      {error && (
        <div className="px-3 py-2 text-sm bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}
      <div
        className={`app ${presentationMode || !canEdit ? 'presentation' : ''}`.trim()}
      >
        <FlowToolbar
          saveStatus={saveStatus}
          canEdit={canEdit}
          status={status}
          dirty={dirty}
          onBack={() => void requestLeave('/qualidade/fluxograma')}
          onSave={() => void handleManualSave()}
          onPublish={() => void handlePublish()}
          publishing={publishing}
          showCreateAiChat={Boolean(
            canEdit && !aiAssistantActive && !viewingHistoricalVersion
          )}
          creatingAiChat={creatingAiChat}
          onCreateAiChat={handleCreateAiChat}
        />
        <div className="app-body">
          {canEdit && !presentationMode && (
            <aside className="sidebar">
              <FlowPalette />
              <PropertiesPanel />
              <ConnectionSettings />
              <div className="sidebar-footer">
                <h3>Atalhos</h3>
                <ul>
                  <li>
                    <kbd>Ctrl</kbd>+<kbd>Z</kbd> desfazer
                  </li>
                  <li>
                    <kbd>Ctrl</kbd>+<kbd>Y</kbd> refazer
                  </li>
                  <li>
                    <kbd>Ctrl</kbd>+<kbd>C</kbd>/<kbd>V</kbd> copiar / colar
                  </li>
                  <li>
                    <kbd>Ctrl</kbd>+<kbd>D</kbd> duplicar
                  </li>
                  <li>
                    <kbd>Delete</kbd> excluir seleção
                  </li>
                  <li>Duplo clique edita o rótulo</li>
                  <li>Solte um nó sobre uma conexão para inseri-lo nela</li>
                  <li>Solte um nó dentro de um grupo para agrupá-lo</li>
                </ul>
              </div>
            </aside>
          )}
          <div className="flow-canvas-wrap" style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
            <FlowCanvas />
            {aiAssistantActive && canEdit && !viewingHistoricalVersion && (
              <FlowAiChat
                open={aiChatOpen}
                onOpen={() => {
                  setAiChatError(null)
                  setAiChatOpen(true)
                }}
                onClose={() => setAiChatOpen(false)}
                messages={aiMessages}
                sending={sendingAiMessage}
                error={aiChatError}
                canSend={canEdit && !sendingAiMessage}
                onSend={handleSendAiMessage}
              />
            )}
          </div>
        </div>
      </div>

      {leaveModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
              Alterações não salvas
            </Heading>
            <Text size="sm" color="muted">
              {status === 'draft'
                ? 'Há alterações não salvas no rascunho. Deseja salvar antes de sair?'
                : 'Há alterações não salvas no fluxograma publicado. Deseja salvar (nova versão) antes de sair?'}
            </Text>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={leaveBusy}
                onClick={cancelLeave}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outline"
                className="text-red-600"
                disabled={leaveBusy}
                onClick={() => void confirmLeaveWithoutSave()}
              >
                Sair sem salvar
              </Button>
              <Button
                type="button"
                variant="solid"
                disabled={leaveBusy}
                onClick={() => void confirmSaveAndLeave()}
              >
                {leaveAction === 'saving-draft' ? 'Salvando…' : 'Salvar e sair'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const FluxogramaEditor: React.FC = () => {
  const params = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = params.id === 'novo' || !params.id
  const modo = searchParams.get('modo')
  const versaoRaw = searchParams.get('versao')
  const versionNumber =
    versaoRaw && /^\d+$/.test(versaoRaw) ? Number(versaoRaw) : null
  // ?modo=edicao força edição; ?modo=apresentacao (ou versão histórica) abre apresentação
  const startInPresentation =
    !isNew &&
    modo !== 'edicao' &&
    (modo === 'apresentacao' || versionNumber != null)

  return (
    <ReactFlowProvider>
      <FluxogramaEditorInner
        key={`${params.id ?? 'novo'}-${versionNumber ?? 'current'}`}
        flowchartId={isNew ? null : params.id!}
        isNew={isNew}
        startInPresentation={startInPresentation}
        versionNumber={versionNumber}
      />
    </ReactFlowProvider>
  )
}

export default FluxogramaEditor
